<!-- TreeView.vue -->
<template>
    <!-- Loading state -->
    <div v-if="loading" class="space-y-3 p-4">
        <div class="flex items-center gap-2 py-2">
            <SkeletonLoader class="h-10 w-48" :loading="true" />
            <SkeletonLoader class="h-6 w-6" :loading="true" />
            <SkeletonLoader class="h-4 w-16" :loading="true" />
        </div>
        <div class="flex items-center gap-2 p-2">
            <SkeletonLoader class="h-4 w-24" :loading="true" />
            <SkeletonLoader class="h-6 w-20" :loading="true" />
            <SkeletonLoader class="h-6 w-24" :loading="true" />
            <SkeletonLoader class="h-6 w-28" :loading="true" />
        </div>
        <div class="space-y-2">
            <div v-for="n in 5" :key="`skeleton-${n}`" class="flex items-center gap-2 py-2">
                <SkeletonLoader class="h-4 w-4" :loading="true" />
                <SkeletonLoader class="h-4 w-32" :loading="true" />
            </div>
        </div>
    </div>

    <!-- Normal content -->
    <div v-else-if="treeNodes && treeLevels.length > 0">
        <div class="flex items-center gap-2 py-2">
            <div v-if="allowSearch" class="flex items-center gap-2 py-2 w-1/2">
                <Input v-model="rawSearch" type="text" :placeholder="`Search: ${treeLevels.map((l) => l.schemaLevel).join(', ')}`" class="w-full" />
            </div>
            <Checkbox
                v-if="visible.length && !searchTerm"
                :checked="selectAll"
                :disabled="allSelectedDisabled(treeNodes)"
                @update:modelValue="selectAllNodes" />
            <Label v-if="visible.length && !searchTerm" class="cursor-pointer">Select all</Label>
        </div>

        <div class="flex items-center gap-2 p-2 has-parent-border" :class="{ 'bg-background': !background }" :style="{ background }">
            <div v-if="visible.length" class="text-sm font-medium text-muted-foreground">
                {{ treeNodes.length }} {{ treeLevels[0].schemaLevel }}{{ treeNodes.length !== 1 ? 's' : '' }}
            </div>
            <Button
                v-if="editMode && !disableCreateAndEdit"
                variant="outline"
                size="sm"
                @click.prevent="addNode(treeNodes, treeLevels[0].schemaLevel, treeLevels[0].schemaObject)"
                class="h-6 px-1.5 text-xs">
                <Plus class="h-3 w-3 mr-1" />
                Add {{ treeLevels[0]?.schemaLevel || 'Item' }}
            </Button>
            <DropdownMenu v-if="editMode && !disableCreateAndEdit">
                <DropdownMenuTrigger as-child>
                    <Button variant="outline" size="sm" class="h-7 px-2 text-xs">
                        <Upload class="h-3 w-3 mr-1" />
                        CSV Actions
                        <ChevronDown class="h-3 w-3 ml-1" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuLabel>Upload</DropdownMenuLabel>
                    <DropdownMenuItem @click="openFileUpload">
                        <Upload class="h-3 w-3 mr-2" />
                        Import {{ treeLevels[0]?.schemaLevel || 'Item' }} .CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="treeLevels.length >= 2" @click="openFullCsvUpload">
                        <Upload class="h-3 w-3 mr-2" />
                        Import Full CSV (All levels)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Download</DropdownMenuLabel>
                    <DropdownMenuItem @click="downloadCsvTemplate(treeLevels[0]?.schemaLevel || 'Item')">
                        <Download class="h-3 w-3 mr-2" />
                        Download {{ treeLevels[0]?.schemaLevel || 'Item' }} .CSV Template
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="treeLevels.length >= 2" @click="downloadFullCsvTemplate">
                        <Download class="h-3 w-3 mr-2" />
                        Download Full .CSV Template
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="treeLevels.length >= 2" @click="downloadCurrentTree" :disabled="isTreeEmpty">
                        <Download class="h-3 w-3 mr-2" />
                        Download Current Tree .CSV
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <input ref="fileInput" type="file" accept=".csv" class="hidden" @change="loadCsvFile" />
            <input ref="fullCsvInput" type="file" accept=".csv" class="hidden" @change="loadFullCsvFile" />
        </div>

        <div class="max-h-70-dynamic" v-if="visible.length && treeLevels.length > 0">
            <RecycleScroller
                :items="visible"
                key-field="id"
                variable
                size-field="size"
                :default-size="MAX_ROW_HEIGHT"
                class="max-h-40-dynamic overflow-y-auto">
                <template #default="{ item }">
                    <div :style="{ paddingLeft: `${item.depth * indent}rem` }">
                        <TreeNode
                            v-memo="[
                                item.id,
                                item.node.name,
                                item.isNodeChecked,
                                item.isError,
                                item.isEditing,
                                collapsedMap[item.nodeId || item.node.id],
                                item.hasMatchingChildren,
                                item.filteredChildCount,
                                item.size
                            ]"
                            :node="item.node"
                            :parent="item.parent"
                            :level="item.depth"
                            :collapsed="collapsedMap[item.nodeId || item.node.id]"
                            :toggle-collapse="toggleCollapsed"
                            :tree-levels="treeLevels"
                            :has-matching-children="item.hasMatchingChildren"
                            :filtered-child-count="item.filteredChildCount"
                            @select-toggled="
                                () => {
                                    try {
                                        // Propagate selection within virtual group structures first
                                        propagateSelectionInVisibleNodes();
                                        // Then propagate within original tree structure
                                        const nodes = props?.treeNodes;
                                        if (nodes && Array.isArray(nodes) && nodes.length > 0) {
                                            propagateSelection(nodes);
                                            // WORKAROUND: Force Vue reactivity by triggering array mutation
                                            // Vue 3's deep watcher doesn't always detect nested property changes
                                            // in complex tree structures with virtual group nodes.
                                            // The push/pop sequence changes the array reference without affecting content,
                                            // ensuring parent watchers detect the selection state changes.
                                            nodes.push({});
                                            nodes.pop();
                                        } else {
                                            // Fallback: propagate selection within visible structure only
                                            propagateSelectionWithinVisible();
                                        }
                                        // Trigger duplicate checking to ensure parent component gets updated selection state
                                        canSaveDuplicate();
                                    } catch (error) {
                                        // TODO: Show user-facing error toast instead of console logging
                                        console.error('[TreeView] Selection propagation failed:', error);
                                    }
                                }
                            "
                            :readonly="readonly"
                            :display-only-selected="displayOnlySelected"
                            :edit-mode="editMode"
                            :disable-create-and-edit="disableCreateAndEdit"
                            :background="background"
                            :custom-width="customWidth"
                            :inputs="inputs"
                            :get-input-value="getInputValue"
                            :handle-input-change="handleInputChange"
                            @duplicate="canSaveDuplicate" />
                    </div>
                </template>
            </RecycleScroller>
        </div>
        <div v-else class="text-center text-muted-foreground py-4">
            <p v-if="treeLevels[0]">No {{ (treeLevels[0]?.schemaLevel || 'item').toLowerCase() }}s found</p>
            <p v-else>No schema found</p>
        </div>
    </div>
</template>

<script>
import { reactive, toRefs, ref, onMounted, watch, computed, provide, shallowReactive } from 'vue';
import { Plus, Upload, Download, ChevronDown } from 'lucide-vue-next';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import TreeNode from './TreeNode.vue';
import Papa from 'papaparse';
import _ from 'lodash';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { toast } from '@/composables/useToast';

export default {
    components: {
        Plus,
        Upload,
        Download,
        ChevronDown,
        Checkbox,
        Button,
        Input,
        Label,
        SkeletonLoader,
        TreeNode,
        RecycleScroller,
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
        DropdownMenuSeparator,
        DropdownMenuLabel
    },
    props: {
        treeNodes: { type: Array, required: true },
        treeLevels: { type: Array, required: true },
        readonly: { type: Boolean, default: false },
        displayOnlySelected: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        disableCreateAndEdit: { type: Boolean, default: false },
        background: { type: String, default: '' },
        addDefaultEmptyNode: { type: Boolean, default: false },
        customWidth: { type: String, default: '95%' },
        allowSearch: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        groupBy: { type: [String, Object, Array], default: null },
        // groupBy examples:
        // "id" - group by 'id' property at deepest level
        // {key: "id"} - group by 'id' at all levels
        // {level: 1, key: "id"} - group by 'id' at depth 1 only
        // {level: 1, key: "id", labelKey: "name"} - group by 'id', display "name" as label
        // [{level: 1, key: "id"}, {level: 2, key: "name"}] - multiple level configs
        groupDisplayLabelKey: { type: [String, Object, Array], default: null },
        // groupDisplayLabelKey examples:
        // "name" - use 'name' property as display label for all grouped levels
        // {level: 1, labelKey: "name"} - use 'name' as label at depth 1 only
        // [{level: 1, labelKey: "transform_name"}] - multiple level configs
        // Note: Can also be specified directly in groupBy config as {key: "id", labelKey: "name"}
        groupPrefix: { type: String, default: 'GROUP - ' },
        // groupPrefix examples:
        // "GROUP - " (default) - displays as "GROUP - TransformName"
        // "" or null - displays as "TransformName" (no prefix)
        // "Transform: " - displays as "Transform: TransformName"

        inputs: { type: Object, default: () => ({}) }
        // inputs: Interactive input controls that can be attached to tree nodes at different levels
        // Supports three organizational levels: global, group, and item
        //
        // Structure: { global: [...], group: [...], item: [...] }
        //
        // Input Configuration Object:
        // {
        //   type: 'checkbox' | 'text' | 'number' | 'select',  // Input type
        //   key: string,                                        // Unique identifier for this input
        //   label: string,                                      // Display label
        //   value: any | Function,                              // Static value or dynamic function (groupId) => value
        //   tooltip?: string,                                   // Optional tooltip text to show on info icon hover
        //   options?: [...],                                    // For select type inputs
        //   placeholder?: string,                               // Placeholder text
        //   disabled?: boolean | Function                       // Static or dynamic disable state
        // }
        //
        // Level Behavior:
        // - global: Inputs shown once at the top of the tree, apply to all nodes
        // - group: Inputs shown on virtual group nodes (created by groupBy prop)
        // - item: Inputs shown on individual leaf items (not currently used)
        //
        // Example 1: Simple checkbox per group
        // inputs: {
        //   global: [],
        //   group: [{
        //     type: 'checkbox',
        //     key: 'enabled',
        //     label: 'Enable this group',
        //     value: false  // Default value for all groups
        //   }],
        //   item: []
        // }
        //
        // Example 2: Dynamic checkbox with computed default values and tooltip
        // inputs: computed(() => ({
        //   global: [],
        //   group: [{
        //     type: 'checkbox',
        //     key: 'auto-discovery',
        //     label: 'Enable auto-discovery',
        //     tooltip: 'When enabled, new topics will be automatically added to this group',
        //     value: (groupId) => {
        //       // Dynamic value based on saved state or business rules
        //       const savedValue = savedState.value.group[groupId]?.['auto-discovery'];
        //       return savedValue !== undefined
        //         ? savedValue
        //         : computeDefaultValue(groupId);
        //     }
        //   }],
        //   item: []
        // }))
        //
        // Example 3: Multiple inputs with different types
        // inputs: {
        //   global: [{
        //     type: 'text',
        //     key: 'prefix',
        //     label: 'Global Prefix',
        //     value: '',
        //     placeholder: 'Enter prefix...'
        //   }],
        //   group: [
        //     {
        //       type: 'checkbox',
        //       key: 'enabled',
        //       label: 'Enable',
        //       value: true
        //     },
        //     {
        //       type: 'select',
        //       key: 'mode',
        //       label: 'Processing Mode',
        //       value: 'auto',
        //       options: [
        //         { value: 'auto', label: 'Automatic' },
        //         { value: 'manual', label: 'Manual' }
        //       ]
        //     }
        //   ],
        //   item: []
        // }
        //
        // Handling Input Changes:
        // Listen to @input-change event which emits:
        // { level: 'global'|'group'|'item', key: string, value: any, nodeId?: string }
        //
        // @input-change="handleInputChange"
        //
        // handleInputChange(event) {
        //   const { level, key, value, nodeId } = event;
        //   if (level === 'group') {
        //     if (!myState.group[nodeId]) myState.group[nodeId] = {};
        //     myState.group[nodeId][key] = value;
        //   }
        // }
    },
    emits: ['can-save-duplicate', 'input-change'],
    setup(props, { emit }) {
        const selectAll = ref(false);
        const fileInput = ref(null);
        const rawSearch = ref('');
        const searchTerm = ref('');
        const collapsedMap = shallowReactive({});
        const sizeMap = shallowReactive({});
        const editingMap = shallowReactive({});

        watch(
            rawSearch,
            _.debounce((val) => {
                searchTerm.value = val;
            }, 300)
        );

        const indent = 2; // rem per level

        const ROW_HEIGHT_TEXT = 36; // base row height in px for text-only nodes (py-1 = ~8px + pb-1 = 4px + content)
        const ROW_HEIGHT_INPUT = 48; // row height in px when showing Input component (h-7 = 28px + py-1 = 8px + pb-1 = 4px)
        const TOOLBAR_HEIGHT = 40; // height of the per-node toolbar in px (py-1.5 px-2 + mt-4px + pb-1 + content)
        const INPUT_SECTION_TOTAL_HEIGHT = 40; // total height for input section (pt-2 = 8px + checkbox with padding ~24px + pb-1 = 4px + space-y-1 = 4px)
        const MAX_ROW_HEIGHT = ROW_HEIGHT_INPUT + TOOLBAR_HEIGHT;

        function downloadCsvTemplate(schemaLevel) {
            // Build five example values, one per line
            const examples = Array.from({ length: 5 }, (_, i) => `${schemaLevel}-${i + 1}`);
            // Join them with newlines (CRLF) and add a final newline
            const csvContent = examples.join('\r\n') + '\r\n';

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${schemaLevel.toLowerCase()}-template.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }

        function downloadFullCsvTemplate() {
            if (props.treeLevels.length < 2) return;

            const schemaLevel = props.treeLevels[0].schemaLevel;
            const tableLevel = props.treeLevels[1].schemaLevel;

            // Generate multi-level examples: schema,table format
            const examples = [
                `${schemaLevel}-1,${tableLevel}-1`,
                `${schemaLevel}-1,${tableLevel}-2`,
                `${schemaLevel}-1,${tableLevel}-3`,
                `${schemaLevel}-2,${tableLevel}-1`,
                `${schemaLevel}-2,${tableLevel}-2`,
                `${schemaLevel}-3,${tableLevel}-1`
            ];

            const csvContent = examples.join('\r\n') + '\r\n';

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${schemaLevel.toLowerCase()}-${tableLevel.toLowerCase()}-template.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }

        function downloadCurrentTree() {
            if (!props.treeNodes?.length || props.treeLevels.length < 2) return;

            const rows = [];

            function collectRows(nodes, schemaName = null) {
                for (const node of nodes) {
                    if (node.schemaLevel === props.treeLevels[0].schemaLevel) {
                        // This is a schema-level node
                        schemaName = node.name;
                        if (node.children?.length) {
                            collectRows(node.children, schemaName);
                        }
                    } else if (node.schemaLevel === props.treeLevels[1].schemaLevel && schemaName) {
                        // This is a table-level node with a parent schema
                        rows.push(`${schemaName},${node.name}`);
                    }
                }
            }

            collectRows(props.treeNodes);

            if (rows.length === 0) return;

            const csvContent = rows.join('\r\n') + '\r\n';
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'current-tree.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }

        function clearAllErrors(nodes) {
            for (const n of nodes) {
                if (n.isError != null) delete n.isError;
                if (n.children) clearAllErrors(n.children);
            }
        }

        // === SIZE CALCULATION SYSTEM ===
        /**
         * Consolidated size calculation system for virtual scrolling
         * Handles all node types and edit states efficiently
         */
        function computeNodeSize(node) {
            // Handle virtual group nodes
            if (node.isGroupNode) {
                return computeGroupNodeSize(node);
            }

            // Handle regular tree nodes
            return computeRegularNodeSize(node);
        }

        function computeGroupNodeSize(node) {
            let size = ROW_HEIGHT_TEXT;

            // Add extra space for expanded groups with children
            const isCollapsed = collapsedMap[node.id];
            const hasChildren = node.children?.length > 0;

            // Add height for group inputs if present on virtual group nodes
            if (props.inputs?.group?.length > 0) {
                size += INPUT_SECTION_TOTAL_HEIGHT;
            }

            // Add separator line height for group nodes (just the border, no text)
            if (hasChildren && !isCollapsed) {
                size += 7; // Small height for separator line (1px line + 4px margin-top + 2px padding-bottom)
            }

            return size;
        }

        function computeRegularNodeSize(node) {
            const nodeLevel = props.treeLevels.findIndex((l) => l.schemaLevel === node.schemaLevel);
            const hasNextLevel = nodeLevel >= 0 && nodeLevel < props.treeLevels.length - 1;
            const isCollapsed = collapsedMap[node.id];
            const isEditing = editingMap[node.id];

            // Base height depends on edit state
            const baseHeight = isEditing ? ROW_HEIGHT_INPUT : ROW_HEIGHT_TEXT;

            // Note: We don't add input height for regular nodes here because:
            // - Individual nodes (children of grouped nodes) don't show inputs
            // - Only group nodes show group-level inputs
            // - Group nodes handle their own input sizing in computeGroupNodeSize

            if (props.editMode) {
                return computeEditModeSize(baseHeight, hasNextLevel, isCollapsed, isEditing);
            } else {
                return computeReadOnlySize(baseHeight, hasNextLevel, isCollapsed);
            }
        }

        function computeEditModeSize(baseHeight, hasNextLevel, isCollapsed, isEditing) {
            if (hasNextLevel && !isCollapsed) {
                // Node with toolbar expanded
                let size = baseHeight + TOOLBAR_HEIGHT + 5; // it was + 8

                if (props.disableCreateAndEdit) {
                    size -= 12;
                }

                if (!isEditing && !props.disableCreateAndEdit) {
                    size -= 8;
                }

                return size;
            } else {
                // Leaf node or collapsed node
                let size = baseHeight;

                if (props.disableCreateAndEdit) {
                    size -= 5;
                }

                if (!isEditing && !props.disableCreateAndEdit) {
                    size -= 5;
                }

                return size;
            }
        }

        function computeReadOnlySize(baseHeight, hasNextLevel, isCollapsed) {
            if (hasNextLevel && !isCollapsed) {
                return baseHeight + TOOLBAR_HEIGHT - 12;
            } else {
                return baseHeight - 5;
            }
        }

        /**
         * Ensures a node has a valid cached size, computing and caching if needed
         * Returns the cached or computed size
         */
        function ensureValidNodeSize(node, forceRecalculate = false) {
            if (forceRecalculate || !sizeMap[node.id] || sizeMap[node.id] <= 0) {
                const newSize = computeNodeSize(node);
                sizeMap[node.id] = Math.max(newSize, ROW_HEIGHT_TEXT);
            }
            return sizeMap[node.id];
        }

        /**
         * Bulk update sizes for multiple nodes efficiently
         */
        function updateNodeSizes(nodes) {
            for (const node of nodes) {
                ensureValidNodeSize(node, true);
            }
        }

        // Replace the old computeSize function reference
        const computeSize = computeNodeSize;

        const isTreeEmpty = computed(() => {
            if (!props.treeNodes?.length) return true;
            // Check if any schema has children (tables)
            return !props.treeNodes.some((schema) => schema.children?.length && schema.schemaLevel === props.treeLevels[0]?.schemaLevel);
        });

        // === SEARCH UTILITY FUNCTIONS ===
        /**
         * Determines if a node or its children match the search term
         * Also handles special cases for virtual group matching during search
         */
        function matchesSearchTerm(node, term, potentialGroupLabel = null) {
            if (!term) return true;

            // Check node name
            if (node.name.toLowerCase().includes(term)) return true;

            // Check potential group label if provided (for virtual group nodes)
            if (potentialGroupLabel && potentialGroupLabel.toLowerCase().includes(term)) return true;

            // SPECIAL CASE: When searching for terms that could match virtual group labels,
            // include parent nodes so their children can be grouped and potentially matched
            if (node.children?.length > 0) {
                const childDepth = getNodeDepth(node) + 1;
                const childGroupConfig = getGroupingConfigForLevel(childDepth);
                if (childGroupConfig) {
                    // Check if search term might match "GROUP - ..." labels
                    const couldMatchGroup = 'group'.includes(term) || (childGroupConfig.key && childGroupConfig.key.toLowerCase().includes(term));

                    if (couldMatchGroup) {
                        return true;
                    }
                }
            }

            // Recursively check children
            return node.children?.some((child) => matchesSearchTerm(child, term));
        }

        /**
         * Helper function to determine a node's depth in the tree structure
         * Maps node's schemaLevel to its position in the treeLevels array
         */
        function getNodeDepth(node) {
            return props.treeLevels.findIndex((level) => level.schemaLevel === node.schemaLevel) || 0;
        }

        /**
         * Determines if a node should show expand/collapse chevrons during search
         * Accounts for both direct child matches and virtual groups that would be created
         */
        function nodeHasMatchingChildren(node, term) {
            if (!term) return node.children?.length > 0;

            // Check if any direct children match the search term
            const hasDirectChildMatches = node.children?.some((child) => matchesSearchTerm(child, term));
            if (hasDirectChildMatches) return true;

            // Check if grouping at child level would create virtual groups that match search
            const childDepth = getNodeDepth(node) + 1;
            if (shouldApplyGrouping(node.children, childDepth)) {
                const groups = createNodeGroups(node.children, childDepth, term);
                if (groups) {
                    const groupConfig = getGroupingConfigForLevel(childDepth);
                    for (const [groupKey, groupNodes] of Object.entries(groups)) {
                        const groupLabel = getGroupLabel(groupKey, groupConfig, groupNodes);
                        if (groupLabel.toLowerCase().includes(term)) {
                            return true;
                        }
                    }
                }
            }

            return false;
        }

        // === GROUPING UTILITY FUNCTIONS ===
        function getGroupingConfigForLevel(depth) {
            if (!props.groupBy) return null;

            let groupConfig = null;

            // Handle legacy string format: "id" means group by 'id' at deepest level
            if (typeof props.groupBy === 'string') {
                const lastLevelIndex = props.treeLevels.length - 1;
                groupConfig = depth === lastLevelIndex ? { key: props.groupBy } : null;
            }
            // Handle object format: {key: "id"} or {level: 1, key: "id"} or {level: 1, key: "id", labelKey: "name"}
            else if (typeof props.groupBy === 'object' && !Array.isArray(props.groupBy)) {
                const config = props.groupBy;
                if (config.level === undefined) {
                    groupConfig = config.key ? { key: config.key, labelKey: config.labelKey } : null;
                } else {
                    groupConfig = depth === config.level ? { key: config.key, labelKey: config.labelKey } : null;
                }
            }
            // Handle array format: [{level: 1, key: "id"}, {level: 2, key: "name"}]
            else if (Array.isArray(props.groupBy)) {
                const matchingConfig = props.groupBy.find((config) => config.level === depth || config.level === undefined);
                groupConfig = matchingConfig?.key ? { key: matchingConfig.key, labelKey: matchingConfig.labelKey } : null;
            }

            // If no labelKey in groupBy config, check groupDisplayLabelKey prop
            if (groupConfig && !groupConfig.labelKey && props.groupDisplayLabelKey) {
                if (typeof props.groupDisplayLabelKey === 'string') {
                    groupConfig.labelKey = props.groupDisplayLabelKey;
                } else if (typeof props.groupDisplayLabelKey === 'object' && !Array.isArray(props.groupDisplayLabelKey)) {
                    if (props.groupDisplayLabelKey.level === undefined || props.groupDisplayLabelKey.level === depth) {
                        groupConfig.labelKey = props.groupDisplayLabelKey.labelKey;
                    }
                } else if (Array.isArray(props.groupDisplayLabelKey)) {
                    const matchingLabel = props.groupDisplayLabelKey.find((config) => config.level === depth || config.level === undefined);
                    if (matchingLabel?.labelKey) {
                        groupConfig.labelKey = matchingLabel.labelKey;
                    }
                }
            }

            return groupConfig;
        }

        function shouldApplyGrouping(nodes, depth) {
            if (!nodes?.length) return false;

            const groupConfig = getGroupingConfigForLevel(depth);
            if (!groupConfig) return false;

            return nodes.some((node) => node[groupConfig.key] !== undefined);
        }

        /**
         * Generates the display label for a group based on groupConfig and node data
         * @param {string} groupKey - The grouping key value (e.g., "123")
         * @param {Object} groupConfig - The grouping configuration {key, labelKey}
         * @param {Array} groupNodes - Array of nodes in the group (to extract labelKey value)
         * @returns {string} - Formatted group label (e.g., "GROUP - TransformName" or "TransformName" or "GROUP - ID:123")
         */
        function getGroupLabel(groupKey, groupConfig, groupNodes) {
            if (!groupConfig) return groupKey;

            const prefix = props.groupPrefix || '';

            // If labelKey is specified, use the value from the first node in the group
            if (groupConfig.labelKey && groupNodes?.length > 0) {
                const labelValue = groupNodes[0][groupConfig.labelKey];
                if (labelValue !== undefined && labelValue !== null && labelValue !== '') {
                    return `${prefix}${labelValue}`;
                }
            }

            // Fallback to original format: "PREFIX + KEY:value"
            return `${prefix}${groupConfig.key?.toUpperCase()}:${groupKey}`;
        }

        function createNodeGroups(nodes, depth, searchTerm) {
            const groupConfig = getGroupingConfigForLevel(depth);
            if (!groupConfig) return null;

            // Filter nodes that should be included in grouping
            const filteredNodes = nodes.filter((node) => {
                if (!searchTerm) return true;

                // Include if node itself matches
                if (matchesSearchTerm(node, searchTerm)) return true;

                // Include if node would be part of a group whose name would match search
                const groupValue = node[groupConfig.key];
                if (groupValue !== undefined && groupValue !== null && groupValue !== '') {
                    // Generate potential group label (considering labelKey if present)
                    const potentialGroupLabel = getGroupLabel(groupValue, groupConfig, [node]);
                    if (potentialGroupLabel.toLowerCase().includes(searchTerm)) return true;
                }

                return false;
            });

            // Group by property value
            const groups = _.groupBy(filteredNodes, (node) => {
                const groupValue = node[groupConfig.key];
                return groupValue !== undefined && groupValue !== null && groupValue !== '' ? groupValue : '__no_value__';
            });

            // Filter out __no_value__ group entries (nodes without the grouping key)
            const finalGroups = {};
            for (const [key, groupNodes] of Object.entries(groups)) {
                if (key !== '__no_value__') {
                    finalGroups[key] = groupNodes;
                }
            }

            return Object.keys(finalGroups).length > 0 ? finalGroups : null;
        }

        /**
         * Creates a virtual group node for organizing similar child nodes
         */
        function createVirtualGroupNode(groupKey, groupLabel, depth, parentNodes) {
            const groupId = `group_${groupKey}_${depth}`;
            const groupNode = {
                id: groupId,
                name: groupLabel,
                schemaLevel: null,
                isGroupNode: true,
                isSelected: false,
                children: [],
                depth,
                groupKey // Store the grouping key (e.g., transform ID) for inputs
            };

            // Initialize collapsed state for group nodes (expanded by default)
            if (collapsedMap[groupId] === undefined) {
                collapsedMap[groupId] = false;
            }

            // Calculate initial size for virtual scrolling
            const nodeSize = ensureValidNodeSize(groupNode);
            sizeMap[groupId] = nodeSize;

            return {
                id: groupId,
                node: groupNode,
                depth,
                parent: parentNodes,
                size: nodeSize
            };
        }

        // === LIST BUILDING FUNCTIONS ===
        function buildVisibleNodesList() {
            const list = [];
            const term = props.allowSearch && searchTerm.value.trim().toLowerCase();

            function processTreeLevel(nodes, depth = 0, parentNodes = null) {
                if (shouldApplyGrouping(nodes, depth)) {
                    const groups = createNodeGroups(nodes, depth, term);
                    if (groups) {
                        processGroupedNodes(groups, depth, parentNodes, term, list);
                        return;
                    }
                }

                // Regular processing (no grouping)
                for (const node of nodes) {
                    processIndividualNode(node, depth, nodes, term, list);
                }
            }

            function processGroupedNodes(groups, depth, parentNodes, searchTerm, list) {
                for (const [groupKey, groupNodes] of Object.entries(groups)) {
                    const groupConfig = getGroupingConfigForLevel(depth);
                    const groupLabel = getGroupLabel(groupKey, groupConfig, groupNodes);

                    // Check if this group should be visible based on search
                    const groupMatches = !searchTerm || groupLabel.toLowerCase().includes(searchTerm);
                    const groupHasMatchingChildren = groupNodes.some((node) => matchesSearchTerm(node, searchTerm));

                    if (!groupMatches && !groupHasMatchingChildren) {
                        continue;
                    }

                    const groupItem = createVirtualGroupNode(groupKey, groupLabel, depth, parentNodes);
                    groupItem.node.children = groupNodes;
                    groupItem.hasMatchingChildren = groupHasMatchingChildren;

                    // Generate unique list item ID
                    const groupListItemId = `listitem_${groupItem.id}_${depth}_${list.length}`;
                    groupItem.id = groupListItemId;
                    groupItem.nodeId = groupItem.node.id;

                    // Add computed properties for consistency
                    const selectedCount = groupNodes.filter((node) => node.isSelected).length;
                    groupItem.isNodeChecked = selectedCount > 0;
                    groupItem.node.isSelected = groupItem.isNodeChecked;
                    groupItem.areChildrenPartiallySelected = selectedCount > 0 && selectedCount < groupNodes.length;
                    groupItem.filteredChildCount = getFilteredChildCount(groupItem.node, searchTerm);
                    groupItem.isError = false;
                    groupItem.isEditing = false;

                    list.push(groupItem);

                    // Update size after adding properties
                    const updatedSize = ensureValidNodeSize(groupItem.node, true);
                    sizeMap[groupListItemId] = updatedSize;
                    groupItem.size = updatedSize;

                    // Add grouped children if group is not collapsed
                    if (!collapsedMap[groupItem.nodeId]) {
                        for (const node of groupNodes) {
                            const shouldIncludeChild = groupMatches || !searchTerm || matchesSearchTerm(node, searchTerm);
                            if (shouldIncludeChild) {
                                if (groupMatches) {
                                    addNodeDirectly(node, depth + 1, groupNodes, searchTerm, list);
                                } else {
                                    processIndividualNode(node, depth + 1, groupNodes, searchTerm, list);
                                }
                            }
                        }
                    }
                }
            }

            function processIndividualNode(node, depth, parentNodes, searchTerm, list) {
                if (!matchesSearchTerm(node, searchTerm)) return;

                ensureNodeHasId(node);
                const size = ensureNodeSize(node);
                const listItemId = `listitem_${node.id}_${depth}_${list.length}`;

                const listItem = createListItem(node, depth, parentNodes, size, listItemId, searchTerm);
                list.push(listItem);

                if (node.children?.length && !collapsedMap[node.id]) {
                    processTreeLevel(node.children, depth + 1, node.children);
                }
            }

            function addNodeDirectly(node, depth, parentNodes, searchTerm, list) {
                ensureNodeHasId(node);
                const size = ensureNodeSize(node);
                const listItemId = `listitem_${node.id}_${depth}_${list.length}`;

                const listItem = createListItem(node, depth, parentNodes, size, listItemId, searchTerm);
                list.push(listItem);

                if (node.children?.length && !collapsedMap[node.id]) {
                    processTreeLevel(node.children, depth + 1, node.children);
                }
            }

            function createListItem(node, depth, parentNodes, size, listItemId, searchTerm) {
                const hasChildren = node.children?.length > 0;
                return {
                    id: listItemId,
                    nodeId: node.id,
                    node,
                    depth,
                    parent: parentNodes,
                    size,
                    hasMatchingChildren: nodeHasMatchingChildren(node, searchTerm),
                    filteredChildCount: getFilteredChildCount(node, searchTerm),
                    isNodeChecked: hasChildren ? node.children.some((c) => c.isSelected) : node.isSelected,
                    isError: calculateNodeError(node, parentNodes),
                    isEditing: editingMap[node.id]
                };
            }

            // Process the tree starting from root level
            processTreeLevel(props.treeNodes, 0, props.treeNodes);

            // Ensure all list items have valid sizes
            list.forEach((item) => {
                if (!item.size || item.size <= 0) {
                    item.size = ensureValidNodeSize(item.node);
                    sizeMap[item.id] = item.size;
                }
                if (!sizeMap[item.id]) {
                    sizeMap[item.id] = item.size;
                }
            });

            return list;
        }

        // === HELPER UTILITY FUNCTIONS ===
        function ensureNodeHasId(node) {
            if (!node.id) {
                Object.defineProperty(node, 'id', {
                    value: crypto.randomUUID(),
                    writable: false,
                    enumerable: false
                });
            }
        }

        function ensureNodeSize(node) {
            return ensureValidNodeSize(node);
        }

        function calculateNodeError(node, parentNodes) {
            return props.editMode && !props.disableCreateAndEdit
                ? !node.name?.trim() ||
                      node.name.includes('*') ||
                      parentNodes?.some((other) => other !== node && other.name?.trim() === node.name?.trim())
                : false;
        }

        function getFilteredChildCount(node, searchTerm) {
            if (!searchTerm || !node.children?.length) return null;

            // Special handling for virtual group nodes - show count of children within the group
            if (node.isGroupNode) {
                return node.children?.length || 0;
            }

            const childDepth = getNodeDepth(node) + 1;

            // If grouping is applied, count groups instead of individual children
            if (shouldApplyGrouping(node.children, childDepth)) {
                const groups = createNodeGroups(node.children, childDepth, searchTerm);
                if (groups) {
                    let totalVisibleItems = 0;
                    const groupConfig = getGroupingConfigForLevel(childDepth);

                    for (const [groupKey, groupNodes] of Object.entries(groups)) {
                        const groupLabel = getGroupLabel(groupKey, groupConfig, groupNodes);
                        const groupMatches = groupLabel.toLowerCase().includes(searchTerm);
                        const groupHasMatchingChildren = groupNodes.some((child) => matchesSearchTerm(child, searchTerm));

                        if (groupMatches || groupHasMatchingChildren) {
                            if (groupMatches) {
                                // If the group itself matches, count all children in the group
                                totalVisibleItems += groupNodes.length;
                            } else {
                                // If only some children match, count only matching children
                                totalVisibleItems += groupNodes.filter((child) => matchesSearchTerm(child, searchTerm)).length;
                            }
                        }
                    }

                    return totalVisibleItems;
                }
            }

            // For non-grouped scenarios, count individual matching children
            return node.children.filter((child) => matchesSearchTerm(child, searchTerm)).length;
        }

        // Main computed property - now much cleaner!
        const visible = computed(() => buildVisibleNodesList());

        function checkRootErrors() {
            if (!props.editMode || props.disableCreateAndEdit) return;
            // count root‐level names
            const counts = props.treeNodes
                .map((n) => n.name.trim())
                .reduce((acc, name) => {
                    acc[name] = (acc[name] || 0) + 1;
                    return acc;
                }, {});

            // set error flag on each node
            props.treeNodes.forEach((n) => {
                const name = n.name.trim();
                const isEmpty = name === '';
                const containsWild = name.includes('*');
                const isDuplicate = counts[name] > 1;

                n.isError = isEmpty || containsWild || isDuplicate;
            });
        }

        function toggleCollapsed(id) {
            collapsedMap[id] = !collapsedMap[id];
        }

        const checkDuplicates = (nodes) => {
            const names = nodes.map((n) => n.name.trim());

            if (new Set(names).size !== names.length) return true;

            return nodes.some((n) => n.children && checkDuplicates(n.children));
        };

        const checkEmpty = (nodes) => {
            for (const n of nodes) {
                if (!n.name.trim()) return true;
                if (n.children && checkEmpty(n.children)) return true;
            }
            return false;
        };
        const canSaveDuplicate = () => {
            const dup = checkDuplicates(props.treeNodes);
            const empty = checkEmpty(props.treeNodes);
            const noNodes = !props.treeNodes || props.treeNodes.length === 0;

            emit('can-save-duplicate', !dup && !empty && !noNodes);
        };

        function addNode(nodes, schemaLevel, schemaObject, customName = null, fromCsv = false) {
            const placeholder = customName ?? `Enter ${schemaLevel} name`;
            const entry = {
                name: fromCsv ? customName : '',
                isSelected: true,
                collapsed: false,
                schemaLevel,
                schemaObject,
                children: [],
                placeholder
            };
            Object.defineProperty(entry, 'id', {
                value: crypto.randomUUID(),
                writable: false,
                enumerable: false
            });
            nodes.push(entry);
            collapsedMap[entry.id] = false;
            ensureValidNodeSize(entry);
            editingMap[entry.id] = true;

            canSaveDuplicate();
        }

        function removeNode(parentArr, node) {
            const idx = parentArr.indexOf(node);
            if (idx !== -1) {
                parentArr.splice(idx, 1);

                canSaveDuplicate();
            }
        }

        function selectAllNodes(newValue) {
            // Handle tristate checkbox values and default toggle behavior
            const selectAllValue = newValue === true || (newValue === undefined && !selectAll.value);

            // Recursively select/deselect all nodes in the tree, not just visible ones
            function recursivelySelectNodes(nodes) {
                for (const node of nodes) {
                    if (!node.disabled) {
                        node.isSelected = selectAllValue;
                    }
                    if (node.children?.length) {
                        recursivelySelectNodes(node.children);
                    }
                }
            }

            recursivelySelectNodes(props.treeNodes);

            // after toggling all leaves, recalc parent selection
            propagateSelection(props.treeNodes);

            // ensure the selectAll state is correct
            selectAll.value = allSelected(props.treeNodes);
        }

        function enableAllEditing() {
            // first ensure every node has an entry
            visible.value.forEach(({ node }) => {
                if (!(node.id in editingMap)) editingMap[node.id] = true;
                else editingMap[node.id] = true;
            });
        }

        // function closeAllEditing() {
        //     visible.value.forEach(({ node, parent, id }) => {
        //         const name = (node.name || '').trim();
        //         const isEmpty = name === '';
        //         const containsWild = name.includes('*');
        //         const isDuplicate = parent.some((o) => o !== node && (o.name || '').trim() === name);
        //         if (!isEmpty && !containsWild && !isDuplicate) {
        //             editingMap[id] = false;
        //         }
        //     });

        // }

        // const selectAllNodes = () => {
        //     const recurse = (nodes, sel) => {
        //         for (const n of nodes) {
        //             n.isSelected = sel;
        //             if (n.children) recurse(n.children, sel);
        //         }
        //     };
        //     recurse(props.treeNodes, selectAll.value);
        // };
        /**
         * Check that all non-disabled leaves are selected, and parents selected if all their children are.
         */
        const allSelected = (nodes) =>
            nodes.every((n) => {
                if (n.disabled) return false;
                if (!n.children || !n.children.length) return n.isSelected;
                return n.isSelected && allSelected(n.children);
            });

        const allSelectedDisabled = (nodes) =>
            nodes.every((n) => {
                if (n.disabled) return true;
                return n.disabled && allSelectedDisabled(n.children);
            });

        function propagateSelection(nodes) {
            for (const n of nodes) {
                if (n.children?.length) {
                    // first fix up grandchildren
                    propagateSelection(n.children);
                    // then enforce parent = any(child.isSelected)
                    n.isSelected = n.children.some((c) => c.isSelected);
                }
            }
        }

        function propagateSelectionInVisibleNodes() {
            // Handle selection propagation within virtual group structures created during search/grouping
            const currentVisible = visible.value;

            for (const listItem of currentVisible) {
                if (listItem.node.isGroupNode && listItem.node.children?.length) {
                    // For virtual group nodes, update their selection state based on their children
                    const hasSelectedChildren = listItem.node.children.some((child) => child.isSelected);

                    // Update the group node's selection state
                    listItem.node.isSelected = hasSelectedChildren;

                    // Update the computed properties for the list item
                    listItem.isNodeChecked = hasSelectedChildren;

                    // Update the indeterminate state for partial selection (checked with some but not all selected)
                    const selectedCount = listItem.node.children.filter((child) => child.isSelected).length;
                    const totalCount = listItem.node.children.length;
                    listItem.areChildrenPartiallySelected = selectedCount > 0 && selectedCount < totalCount;
                }
            }
        }

        function propagateSelectionWithinVisible() {
            // Fallback propagation when original tree data is not available during search
            // Since we only have the visible structure, propagate within virtual groups
            propagateSelectionInVisibleNodes();
        }

        watch(
            () => props.treeNodes,
            (newTree, oldTree) => {
                // // Detect transition from empty to populated (async loading scenario)
                // const wasEmpty = !oldTree || oldTree.length === 0;
                // const isPopulated = newTree && newTree.length > 0;

                // if (wasEmpty && isPopulated) {
                //     // Re-initialize tree when data loads after empty mount
                //     initializeTreeNodes(newTree, true);
                // }
                initializeTreeNodes(newTree);

                selectAll.value = allSelected(newTree);
                checkRootErrors();
            },
            { deep: true }
        );

        // Watch for changes to treeLevels to handle async loading scenarios
        watch(
            () => props.treeLevels,
            (newLevels, oldLevels) => {
                // Handle transition from empty to populated tree levels (async loading scenario)
                const wasEmpty = !oldLevels || oldLevels.length === 0;
                const isPopulated = newLevels && newLevels.length > 0;

                if (wasEmpty && isPopulated) {
                    // Force template reactivity update by ensuring all computed properties re-evaluate
                    handleDefaultEmptyNodes();
                }
            },
            { deep: true }
        );

        watch(
            () => ({ ...editingMap, ...collapsedMap, editMode: props.editMode }),
            () => {
                // Efficiently update sizes for all visible nodes
                const nodesToUpdate = visible.value.map((listItem) => listItem.node);
                updateNodeSizes(nodesToUpdate);

                // Update list item sizes with the cached values
                visible.value.forEach((listItem) => {
                    listItem.size = sizeMap[listItem.node.id];
                    sizeMap[listItem.id] = listItem.size; // Also cache using list item ID
                });
            },
            { deep: true }
        );

        // Watch for input configuration changes and recalculate sizes
        watch(
            () => props.inputs,
            () => {
                // When inputs configuration changes, invalidate and recalculate all sizes
                const nodesToUpdate = visible.value.map((listItem) => listItem.node);
                updateNodeSizes(nodesToUpdate);

                // Update list item sizes with the cached values
                visible.value.forEach((listItem) => {
                    listItem.size = sizeMap[listItem.node.id];
                    sizeMap[listItem.id] = listItem.size; // Also cache using list item ID
                });
            },
            { deep: true, immediate: false }
        );

        const openFileUpload = () => {
            if (fileInput.value) {
                fileInput.value.value = '';
                fileInput.value.click();
            }
        };

        const fullCsvInput = ref(null);

        const openFullCsvUpload = () => {
            if (fullCsvInput.value) {
                fullCsvInput.value.value = '';
                fullCsvInput.value.click();
            }
        };
        // === CSV IMPORT UTILITY FUNCTIONS ===
        /**
         * Consolidated CSV import system - handles all CSV import scenarios
         * @param {File} file - The CSV file to import
         * @param {Object} config - Import configuration
         */
        function importCsvFile(file, config) {
            if (!file?.name.toLowerCase().endsWith('.csv')) {
                toast.error('Please upload a valid CSV file');
                return;
            }

            // Validate configuration based on import type
            if (config.type === 'multi-level' && props.treeLevels.length < 2) {
                toast.error('Multi-level import requires at least 2 tree levels configured');
                return;
            }

            const batchSize = 1000;
            let buffer = config.type === 'multi-level' ? new Map() : [];
            let totalRowsProcessed = 0;
            let validRowsProcessed = 0;
            let invalidRowsSkipped = 0;

            const papaConfig = {
                worker: true,
                skipEmptyLines: true,
                step({ data }) {
                    totalRowsProcessed++;

                    if (config.type === 'single-level') {
                        processSingleLevelRow(data, buffer, config);
                    } else if (config.type === 'multi-level') {
                        processMultiLevelRow(data, buffer);
                    } else if (config.type === 'child-level') {
                        processSingleLevelRow(data, buffer, config);
                    }

                    // Process batches when buffer is full
                    const bufferSize = config.type === 'multi-level' ? buffer.size : buffer.length;
                    if (bufferSize >= batchSize) {
                        processBatch(buffer, config);
                        buffer = config.type === 'multi-level' ? new Map() : [];
                    }
                },
                complete() {
                    if ((config.type === 'multi-level' && buffer.size > 0) || (config.type !== 'multi-level' && buffer.length > 0)) {
                        processBatch(buffer, config);
                    }

                    showImportResults(config);
                },
                error(error) {
                    toast.error(`CSV parsing failed: ${error.message}`);
                }
            };

            function processSingleLevelRow(data, buffer, config) {
                const name = data[0]?.trim();
                if (name) {
                    buffer.push(name);
                    validRowsProcessed++;
                } else {
                    invalidRowsSkipped++;
                }
            }

            function processMultiLevelRow(data, buffer) {
                // Validate CSV structure for multi-level import
                if (!data || data.length < 2) {
                    invalidRowsSkipped++;
                    return;
                }

                const schemaName = data[0]?.trim();
                const tableName = data[1]?.trim();

                if (!schemaName || !tableName) {
                    invalidRowsSkipped++;
                    return;
                }

                validRowsProcessed++;

                // Group tables by schema
                if (!buffer.has(schemaName)) {
                    buffer.set(schemaName, new Set());
                }
                buffer.get(schemaName).add(tableName);
            }

            function processBatch(buffer, config) {
                if (config.type === 'multi-level') {
                    processMultiLevelBatch(buffer);
                } else {
                    processSingleLevelBatch(buffer, config);
                }
            }

            function processSingleLevelBatch(names, config) {
                const entries = names.map((name) => createTreeNode(name, config.schemaLevel, config.schemaObject));

                if (config.type === 'child-level') {
                    // Add to parent node's children
                    config.parentNode.children = config.parentNode.children || [];
                    config.parentNode.children.splice(config.parentNode.children.length, 0, ...entries);
                } else {
                    // Add to root level
                    props.treeNodes.splice(props.treeNodes.length, 0, ...entries);
                }
            }

            function processMultiLevelBatch(schemaGroups) {
                for (const [schemaName, tableNames] of schemaGroups.entries()) {
                    let schemaNode = props.treeNodes.find((node) => node.name === schemaName);

                    if (!schemaNode) {
                        schemaNode = createTreeNode(schemaName, props.treeLevels[0].schemaLevel, props.treeLevels[0].schemaObject);
                        props.treeNodes.push(schemaNode);
                    }

                    // Add table nodes to schema
                    for (const tableName of tableNames) {
                        if (!schemaNode.children.find((child) => child.name === tableName)) {
                            const tableNode = createTreeNode(tableName, props.treeLevels[1].schemaLevel, props.treeLevels[1].schemaObject);
                            schemaNode.children.push(tableNode);
                        }
                    }
                }
                schemaGroups.clear();
            }

            function createTreeNode(name, schemaLevel, schemaObject) {
                const node = {
                    name,
                    isSelected: true,
                    collapsed: false,
                    schemaLevel,
                    schemaObject,
                    children: [],
                    placeholder: name
                };

                Object.defineProperty(node, 'id', {
                    value: crypto.randomUUID(),
                    writable: false,
                    enumerable: false
                });

                collapsedMap[node.id] = false;
                ensureValidNodeSize(node);

                return node;
            }

            function showImportResults(config) {
                if (validRowsProcessed === 0) {
                    const errorMsg =
                        config.type === 'multi-level'
                            ? 'Import failed: No valid data found. Expected format: schema,table'
                            : 'Import failed: No valid entries found';
                    toast.error(errorMsg);
                    return;
                }

                const entityName = config.schemaLevel?.toLowerCase() || 'item';
                let message =
                    config.type === 'multi-level'
                        ? `Successfully imported ${validRowsProcessed} entries`
                        : `Successfully imported ${validRowsProcessed} ${entityName}s`;

                if (invalidRowsSkipped > 0) {
                    message += ` (${invalidRowsSkipped} ${config.type === 'multi-level' ? 'invalid' : 'empty'} rows skipped)`;
                    toast.warning(message);
                } else {
                    toast.success(message);
                }

                clearAllErrors(props.treeNodes);
                canSaveDuplicate();

                // Emit event for child-level imports
                if (config.type === 'child-level' && config.onComplete) {
                    config.onComplete();
                }
            }

            Papa.parse(file, papaConfig);
        }

        // Simplified wrapper functions for different import types
        const loadCsvFile = (e) => {
            const file = e.target.files[0];
            importCsvFile(file, {
                type: 'single-level',
                schemaLevel: props.treeLevels[0].schemaLevel,
                schemaObject: props.treeLevels[0].schemaObject
            });
        };

        const loadFullCsvFile = (e) => {
            const file = e.target.files[0];
            importCsvFile(file, {
                type: 'multi-level'
            });
        };

        // Export CSV import utility for TreeNode component
        provide('importCsvFile', importCsvFile);

        // === INPUT HANDLING FUNCTIONS ===
        /**
         * Retrieves the current value for an input at a specific level
         * Handles dynamic value computation for group-level inputs
         */
        function getInputValue(level, nodeId, key) {
            // Safety checks for undefined/null inputs
            if (!props.inputs || typeof props.inputs !== 'object') return null;
            if (!props.inputs[level] || !Array.isArray(props.inputs[level])) return null;

            const inputConfig = props.inputs[level].find((input) => input.key === key);
            if (!inputConfig) return null;

            // If value is a function, call it with the nodeId (for dynamic values)
            if (typeof inputConfig.value === 'function') {
                return inputConfig.value(nodeId);
            }

            // Otherwise return the static value
            return inputConfig.value;
        }

        /**
         * Handles input changes and emits them to the parent component
         * Parent component should listen to @input-change event
         */
        function handleInputChange(level, key, value, nodeId) {
            emit('input-change', {
                level,
                key,
                value,
                nodeId
            });
        }

        function handleDefaultEmptyNodes() {
            // Only add if enabled, treeLevels available, and no existing nodes
            if (!props.addDefaultEmptyNode || !props.treeLevels[0] || props.treeNodes.length > 0) {
                return;
            }

            // Add default node at root level
            addNode(props.treeNodes, props.treeLevels[0].schemaLevel, props.treeLevels[0].schemaObject);

            // If there are multiple levels, also add default child nodes
            if (props.treeLevels.length > 1) {
                const rootNode = props.treeNodes[props.treeNodes.length - 1]; // Get the node we just added
                let currentNode = rootNode;

                // Add default nodes for each subsequent level
                for (let i = 1; i < props.treeLevels.length; i++) {
                    const level = props.treeLevels[i];
                    addNode(currentNode.children, level.schemaLevel, level.schemaObject);

                    currentNode = currentNode.children[currentNode.children.length - 1]; // Move to the child we just added
                }
            }
        }

        // Watch for changes to treeLevels or addDefaultEmptyNode
        watch(
            () => [props.addDefaultEmptyNode, props.treeLevels],
            () => {
                handleDefaultEmptyNodes();
            },
            { immediate: true, deep: true }
        );

        /**
         * Recursively initializes all nodes in the tree with proper IDs and collapsed states
         */
        function initializeTreeNodes(nodes, isRootLevel = true) {
            nodes.forEach((node, index) => {
                // Ensure every node has an ID
                if (!node.id) {
                    Object.defineProperty(node, 'id', {
                        value: crypto.randomUUID(),
                        writable: false,
                        enumerable: false
                    });
                }

                // Initialize collapsed state - preserve existing map state first, then respect node property, then use defaults
                if (collapsedMap[node.id] === undefined) {
                    if (isRootLevel) {
                        // For root level: respect node's collapsed property, default first node expanded
                        collapsedMap[node.id] = node.hasOwnProperty('collapsed') ? node.collapsed : index !== 0;
                    } else {
                        // For nested nodes: respect node's collapsed property, default to collapsed
                        collapsedMap[node.id] = node.hasOwnProperty('collapsed') ? node.collapsed : true;
                    }
                }

                // Initialize size
                ensureValidNodeSize(node);

                // Recursively initialize children
                if (node.children?.length) {
                    initializeTreeNodes(node.children, false);
                }
            });
        }

        onMounted(() => {
            initializeTreeNodes(props.treeNodes, true);

            // Initialize sizes for any existing group nodes in the visible list
            // This will be handled dynamically as groups are created

            // Initial call to handle default empty nodes (if treeLevels already available)
            handleDefaultEmptyNodes();

            selectAll.value = allSelected(props.treeNodes);
        });

        provide(
            'visibleIds',
            computed(() => new Set(visible.value.map((item) => item.id)))
        );

        provide('sizeMap', sizeMap);
        provide('computeSize', computeSize);
        provide('editingMap', editingMap);

        // const anyEditing = computed(() => Object.values(editingMap).some((val) => val));

        return {
            selectAll,
            fileInput,
            fullCsvInput,
            visible,
            isTreeEmpty,
            indent,
            addNode,
            removeNode,
            downloadCsvTemplate,
            downloadFullCsvTemplate,
            downloadCurrentTree,
            searchTerm,
            editingMap,
            rawSearch,
            propagateSelection,
            propagateSelectionInVisibleNodes,
            propagateSelectionWithinVisible,
            selectAllNodes,
            canSaveDuplicate,
            collapsedMap,
            openFileUpload,
            openFullCsvUpload,
            loadCsvFile,
            loadFullCsvFile,
            toggleCollapsed,
            enableAllEditing,
            allSelectedDisabled,
            // closeAllEditing,
            // anyEditing,
            MAX_ROW_HEIGHT,
            sizeMap,
            // Expose props for template access
            treeNodes: computed(() => props.treeNodes),
            treeLevels: computed(() => props.treeLevels),
            // Expose inputs with safe fallback
            inputs: computed(() => props.inputs || {}),
            getInputValue,
            handleInputChange
        };
    }
};
</script>

<style>
@reference '../style.css';

.has-parent-border {
    @apply border-b border-border w-full;
}

:deep(.vue-recycle-scroller__item-wrapper),
:deep(.vue-recycle-scroller__item-view) {
    overflow: visible !important;
}

/* Improved tree visual hierarchy */
.max-h-70-dynamic {
    @apply rounded-md;
}

/* Better visual separation for nested items */
:deep(.tree-node-with-branch) {
    position: relative;
}

/* Subtle hover effect for tree nodes */
:deep(.node-header):hover {
    @apply bg-accent/30 transition-colors duration-150;
}

/* Group node styling enhancements */
:deep(.group-inputs-toolbox) {
    @apply shadow-sm;
}
</style>
